import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { worldCupData } from '../data/worldCupData';
import { getTeamName } from '../locales/teamNames';

const WorldCupPage = () => {
  const { groups, knockout, champion, runnerUp, thirdPlace, year, liveStreams } = worldCupData;
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const currentLang = i18n.language;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const TeamBox = ({ teamKey, isWinner = false, isChampion = false }) => (
    <div className={`team-box ${isWinner ? 'winner' : ''} ${isChampion ? 'champion' : ''} ${!teamKey ? 'empty' : ''}`}>
      <span className="team-name">{teamKey ? getTeamName(teamKey, currentLang) : t('groupStage.toBeAnnounced')}</span>
    </div>
  );

  const MatchPair = ({ team1Key, team2Key, winnerKey, matchInfo }) => {
    const isPending = !team1Key || !team2Key;
    return (
      <div className="match-pair">
        <div className="match-upper">
          <TeamBox teamKey={team1Key} isWinner={team1Key === winnerKey} />
          <div className="vs-connector">
            <span className="vs-text">{t('knockout.vs')}</span>
          </div>
          <TeamBox teamKey={team2Key} isWinner={team2Key === winnerKey} />
        </div>
        {matchInfo && (matchInfo.date || matchInfo.time) ? (
          <div className="match-details">
            <span>{matchInfo.date || t('knockout.dateTbd')}</span>
            <span>{matchInfo.time || ''}</span>
          </div>
        ) : (
          <div className="match-status">
            <span className="pending">{t('knockout.pending')}</span>
          </div>
        )}
      </div>
    );
  };

  const MobileMatchCard = ({ match, label }) => {
    const hasTeams = match.team1.name && match.team2.name;
    const hasDateOrTime = match.date || match.time;
    
    return (
      <div className="mobile-match-card">
        <div className="mobile-match-label">{label}</div>
        <div className="mobile-match-teams">
          <div className={`mobile-team ${match.team1.name === match.winner ? 'mobile-winner' : ''}`}>
            {match.team1.name ? getTeamName(match.team1.name, currentLang) : t('groupStage.toBeAnnounced')}
            <span className="mobile-score">{match.team1.score || '-'}</span>
          </div>
          <div className="mobile-vs">{t('knockout.vs')}</div>
          <div className={`mobile-team ${match.team2.name === match.winner ? 'mobile-winner' : ''}`}>
            {match.team2.name ? getTeamName(match.team2.name, currentLang) : t('groupStage.toBeAnnounced')}
            <span className="mobile-score">{match.team2.score || '-'}</span>
          </div>
        </div>
        <div className="mobile-match-info">
          <span>{match.date || t('knockout.dateTbd')}</span>
          <span>{match.time || ''}</span>
        </div>
        {match.winner ? (
          <div className="mobile-progress-arrow">
            <span>{t('knockout.progress')}{getTeamName(match.winner, currentLang)}</span>
            <span>↓</span>
          </div>
        ) : (
          <div className="mobile-match-status">
            <span className="pending">{t('knockout.pending')}</span>
          </div>
        )}
      </div>
    );
  };

  const LiveStreamSection = () => {
    if (!liveStreams || liveStreams.length === 0) return null;

    return (
      <div className="live-stream-section">
        <h2>{t('liveStream.title')}</h2>
        <div className="live-streams-container">
          {liveStreams.map((stream, index) => (
            <a 
              key={index} 
              href={stream.url || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              className={`stream-link ${!stream.url ? 'disabled' : ''}`}
            >
              {stream.name}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const GroupMatches = ({ matches }) => {
    if (!matches || matches.length === 0) return null;

    const sortedMatches = [...matches].sort((a, b) => {
      const dateA = a.date || '99-99';
      const dateB = b.date || '99-99';
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      const timeA = a.time || '99:99';
      const timeB = b.time || '99:99';
      return timeA.localeCompare(timeB);
    });

    return (
      <div className="group-matches">
        <h4>{t('groupStage.schedule')}</h4>
        {sortedMatches.map((match, index) => (
          <div key={index} className="group-match">
            <div className="match-teams">
              <span className="match-team-left">{match.team1 ? getTeamName(match.team1, currentLang) : t('groupStage.tbd')}</span>
              <span className="match-score">
                {match.score ? `${match.score.team1 || '-'}-${match.score.team2 || '-'}` : '-'}
              </span>
              <span className="match-team-right">{match.team2 ? getTeamName(match.team2, currentLang) : t('groupStage.tbd')}</span>
            </div>
            <div className="match-time">
              {match.date || t('knockout.dateTbd')} {match.time || ''}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const MobileKnockoutTree = () => {
    const allQualified = groups.flatMap(g => g.qualified);
    
    return (
      <div className="mobile-knockout-tree">
        <div className="mobile-round-section">
          <div className="mobile-round-label">{t('knockout.qualifiedTeams')}</div>
          <div className="mobile-teams-grid">
            {allQualified.map((teamKey, i) => (
              <div key={i} className="mobile-qualified-team">{getTeamName(teamKey, currentLang)}</div>
            ))}
          </div>
        </div>

        <div className="mobile-round-section">
          <div className="mobile-round-label">{t('knockout.round32')}</div>
          {knockout.round32.map((match, i) => (
            <MobileMatchCard key={match.match} match={match} label={`${t('knockout.round32')} ${i + 1}`} />
          ))}
        </div>

        <div className="mobile-round-section">
          <div className="mobile-round-label">{t('knockout.round16')}</div>
          {knockout.round16.map((match, i) => (
            <MobileMatchCard key={match.match} match={match} label={`${t('knockout.round16')} ${i + 1}`} />
          ))}
        </div>

        <div className="mobile-round-section">
          <div className="mobile-round-label">{t('knockout.quarterfinals')}</div>
          {knockout.quarterfinals.map((match, i) => (
            <MobileMatchCard key={match.match} match={match} label={`${t('knockout.quarterfinals')} ${i + 1}`} />
          ))}
        </div>

        <div className="mobile-round-section">
          <div className="mobile-round-label">{t('knockout.semifinals')}</div>
          {knockout.semifinals.map((match, i) => (
            <MobileMatchCard key={match.match} match={match} label={`${t('knockout.semifinals')} ${i + 1}`} />
          ))}
        </div>

        <div className="mobile-round-section">
          <div className="mobile-round-label">{t('knockout.final')}</div>
          <MobileMatchCard match={knockout.final} label={t('knockout.final')} />
        </div>

        <div className="mobile-champion-section">
          <div className="mobile-champion-box">
            <TeamBox teamKey={champion} isChampion={true} />
            <div className="mobile-champion-label">{t('podium.champion')}</div>
          </div>
        </div>
      </div>
    );
  };

  const GroupSection = () => (
    <div className="group-section">
      <h2>{t('groupStage.title')}</h2>
      <p className="group-subtitle">{t('groupStage.subtitle')}</p>
      <div className="groups-container">
        {groups.map(group => (
          <div key={group.name} className="group-card">
            <h3 className="group-name">{t('groupStage.groupName')}{group.name}</h3>
            <div className="group-teams">
              {group.teams.map((teamKey, index) => (
                <div 
                  key={index} 
                  className={`team-item ${group.qualified.includes(teamKey) ? 'qualified' : ''} ${!teamKey ? 'empty' : ''}`}
                >
                  <span className="team-name">{teamKey ? getTeamName(teamKey, currentLang) : t('groupStage.toBeAnnounced')}</span>
                  {group.qualified.includes(teamKey) && <span className="qualified-badge">{t('groupStage.qualified')}</span>}
                </div>
              ))}
            </div>
            <div className="qualified-teams">
              <span className="qualified-label">{t('groupStage.qualifiedTeams')}:</span>
              <span className="qualified-names">
                {group.qualified.length > 0 
                  ? group.qualified.map(q => getTeamName(q, currentLang)).join(' / ') 
                  : t('groupStage.toBeAnnounced')
                }
              </span>
            </div>
            <GroupMatches matches={group.matches} />
          </div>
        ))}
      </div>
    </div>
  );

  const KnockoutTree = () => {
    if (isMobile) {
      return <MobileKnockoutTree />;
    }

    const allQualified = groups.flatMap(g => g.qualified);
    
    return (
      <div className="knockout-tree-section">
        <h2>{t('knockout.title')}</h2>
        
        <div className="knockout-tree">
          <div className="tree-layer layer-qualified">
            <h4>{t('knockout.qualifiedTeams')}</h4>
            <div className="layer-content">
              <div className="team-grid">
                {allQualified.slice(0, 8).map((teamKey, i) => (
                  <TeamBox key={i} teamKey={teamKey} />
                ))}
              </div>
              <div className="team-grid">
                {allQualified.slice(8, 16).map((teamKey, i) => (
                  <TeamBox key={i} teamKey={teamKey} />
                ))}
              </div>
              {allQualified.length > 16 && (
                <div className="team-grid">
                  {allQualified.slice(16, 24).map((teamKey, i) => (
                    <TeamBox key={i} teamKey={teamKey} />
                  ))}
                </div>
              )}
              {allQualified.length > 24 && (
                <div className="team-grid">
                  {allQualified.slice(24, 32).map((teamKey, i) => (
                    <TeamBox key={i} teamKey={teamKey} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="tree-connector">
            <svg viewBox="0 0 800 60" className="connector-svg">
              {[0, 1, 2, 3].map(i => (
                <React.Fragment key={i}>
                  <line x1={95 + i * 180} y1="30" x2={95 + i * 180} y2="60" stroke="#1a237e" strokeWidth="3" />
                  <path d={`M ${65 + i * 180} 0 Q ${65 + i * 180} 20 ${95 + i * 180} 30`} stroke="#1a237e" strokeWidth="3" fill="none" />
                  <path d={`M ${125 + i * 180} 0 Q ${125 + i * 180} 20 ${95 + i * 180} 30`} stroke="#1a237e" strokeWidth="3" fill="none" />
                </React.Fragment>
              ))}
            </svg>
          </div>

          <div className="tree-layer layer-16">
            <h4>{t('knockout.round32')}</h4>
            <div className="layer-content">
              {knockout.round32.map((match, i) => (
                <MatchPair 
                  key={match.match} 
                  team1Key={match.team1.name} 
                  team2Key={match.team2.name}
                  winnerKey={match.winner}
                  matchInfo={{ date: match.date, time: match.time }}
                />
              ))}
            </div>
          </div>

          <div className="tree-connector">
            <svg viewBox="0 0 800 60" className="connector-svg">
              {[0, 1].map(i => (
                <React.Fragment key={i}>
                  <line x1={95 + i * 360} y1="30" x2={95 + i * 360} y2="60" stroke="#1a237e" strokeWidth="3" />
                  <line x1={275 + i * 360} y1="30" x2={275 + i * 360} y2="60" stroke="#1a237e" strokeWidth="3" />
                  <path d={`M ${50 + i * 360} 0 Q ${50 + i * 360} 20 ${95 + i * 360} 30`} stroke="#1a237e" strokeWidth="3" fill="none" />
                  <path d={`M ${140 + i * 360} 0 Q ${140 + i * 360} 20 ${95 + i * 360} 30`} stroke="#1a237e" strokeWidth="3" fill="none" />
                  <path d={`M ${230 + i * 360} 0 Q ${230 + i * 360} 20 ${275 + i * 360} 30`} stroke="#1a237e" strokeWidth="3" fill="none" />
                  <path d={`M ${320 + i * 360} 0 Q ${320 + i * 360} 20 ${275 + i * 360} 30`} stroke="#1a237e" strokeWidth="3" fill="none" />
                </React.Fragment>
              ))}
            </svg>
          </div>

          <div className="tree-layer layer-8">
            <h4>{t('knockout.round16')}</h4>
            <div className="layer-content">
              {knockout.round16.map((match, i) => (
                <MatchPair 
                  key={match.match} 
                  team1Key={match.team1.name} 
                  team2Key={match.team2.name}
                  winnerKey={match.winner}
                  matchInfo={{ date: match.date, time: match.time }}
                />
              ))}
            </div>
          </div>

          <div className="tree-connector">
            <svg viewBox="0 0 800 60" className="connector-svg">
              <line x1="185" y1="30" x2="185" y2="60" stroke="#1a237e" strokeWidth="3" />
              <line x1="545" y1="30" x2="545" y2="60" stroke="#1a237e" strokeWidth="3" />
              <path d="M 95 0 Q 95 20 185 30" stroke="#1a237e" strokeWidth="3" fill="none" />
              <path d="M 275 0 Q 275 20 185 30" stroke="#1a237e" strokeWidth="3" fill="none" />
              <path d="M 455 0 Q 455 20 545 30" stroke="#1a237e" strokeWidth="3" fill="none" />
              <path d="M 635 0 Q 635 20 545 30" stroke="#1a237e" strokeWidth="3" fill="none" />
            </svg>
          </div>

          <div className="tree-layer layer-4">
            <h4>{t('knockout.quarterfinals')}</h4>
            <div className="layer-content">
              {knockout.quarterfinals.map((match, i) => (
                <MatchPair 
                  key={match.match} 
                  team1Key={match.team1.name} 
                  team2Key={match.team2.name}
                  winnerKey={match.winner}
                  matchInfo={{ date: match.date, time: match.time }}
                />
              ))}
            </div>
          </div>

          <div className="tree-connector">
            <svg viewBox="0 0 800 60" className="connector-svg">
              <line x1="365" y1="30" x2="365" y2="60" stroke="#ffc107" strokeWidth="3" />
              <path d="M 185 0 Q 275 20 365 30" stroke="#ffc107" strokeWidth="3" fill="none" />
              <path d="M 545 0 Q 455 20 365 30" stroke="#ffc107" strokeWidth="3" fill="none" />
            </svg>
          </div>

          <div className="tree-layer layer-2">
            <h4>{t('knockout.semifinals')}</h4>
            <div className="layer-content">
              {knockout.semifinals.map((match, i) => (
                <MatchPair 
                  key={match.match} 
                  team1Key={match.team1.name} 
                  team2Key={match.team2.name}
                  winnerKey={match.winner}
                  matchInfo={{ date: match.date, time: match.time }}
                />
              ))}
            </div>
          </div>

          <div className="tree-connector">
            <svg viewBox="0 0 800 60" className="connector-svg">
              <line x1="365" y1="30" x2="365" y2="60" stroke="#ffc107" strokeWidth="3" />
              <path d="M 185 0 Q 275 20 365 30" stroke="#ffc107" strokeWidth="3" fill="none" />
              <path d="M 545 0 Q 455 20 365 30" stroke="#ffc107" strokeWidth="3" fill="none" />
            </svg>
          </div>

          <div className="tree-layer layer-final">
            <h4>{t('knockout.final')}</h4>
            <div className="layer-content">
              <MatchPair 
                key={knockout.final.match} 
                team1Key={knockout.final.team1.name} 
                team2Key={knockout.final.team2.name}
                winnerKey={knockout.final.winner}
                matchInfo={{ date: knockout.final.date, time: knockout.final.time }}
              />
            </div>
          </div>

          <div className="tree-connector">
            <svg viewBox="0 0 800 80" className="connector-svg">
              <line x1="365" y1="0" x2="365" y2="80" stroke="#ffc107" strokeWidth="3" />
            </svg>
          </div>

          <div className="champion-layer">
            <TeamBox teamKey={champion} isChampion={true} />
            <div className="champion-label">{t('podium.champion')}</div>
          </div>
        </div>
      </div>
    );
  };

  const ChampionSection = () => {
    const hasChampion = champion || runnerUp || thirdPlace;
    
    if (isMobile) {
      return (
        <div className="champion-section">
          <h2>{t('podium.title')}</h2>
          {hasChampion ? (
            <div className="podium-mobile">
              <div className="podium-mobile-item first">
                <div className="podium-medal gold">🏆</div>
                <div className="podium-mobile-rank">1</div>
                <div className="podium-mobile-team champion">{champion ? getTeamName(champion, currentLang) : t('groupStage.toBeAnnounced')}</div>
                <div className="podium-mobile-label">{t('podium.champion')}</div>
              </div>
              
              <div className="podium-mobile-row">
                <div className="podium-mobile-item second">
                  <div className="podium-medal silver">🥈</div>
                  <div className="podium-mobile-rank">2</div>
                  <div className="podium-mobile-team">{runnerUp ? getTeamName(runnerUp, currentLang) : t('groupStage.toBeAnnounced')}</div>
                  <div className="podium-mobile-label">{t('podium.runnerUp')}</div>
                </div>
                <div className="podium-mobile-item third">
                  <div className="podium-medal bronze">🥉</div>
                  <div className="podium-mobile-rank">3</div>
                  <div className="podium-mobile-team">{thirdPlace ? getTeamName(thirdPlace, currentLang) : t('groupStage.toBeAnnounced')}</div>
                  <div className="podium-mobile-label">{t('podium.thirdPlace')}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="podium-placeholder">
              <div className="placeholder-icon">🏆</div>
              <p>{t('podium.placeholder')}</p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="champion-section">
        <h2>{t('podium.title')}</h2>
        {hasChampion ? (
          <div className="podium">
            <div className="podium-item second">
              <div className="podium-stand">2</div>
              <div className="podium-team">{runnerUp ? getTeamName(runnerUp, currentLang) : t('groupStage.toBeAnnounced')}</div>
              <div className="podium-label">{t('podium.runnerUp')}</div>
            </div>
            <div className="podium-item first">
              <div className="podium-stand">1</div>
              <div className="podium-team champion">{champion ? getTeamName(champion, currentLang) : t('groupStage.toBeAnnounced')}</div>
              <div className="podium-label">{t('podium.champion')}</div>
            </div>
            <div className="podium-item third">
              <div className="podium-stand">3</div>
              <div className="podium-team">{thirdPlace ? getTeamName(thirdPlace, currentLang) : t('groupStage.toBeAnnounced')}</div>
              <div className="podium-label">{t('podium.thirdPlace')}</div>
            </div>
          </div>
        ) : (
          <div className="podium-placeholder">
            <div className="placeholder-icon">🏆</div>
            <p>{t('podium.placeholder')}</p>
          </div>
        )}
      </div>
    );
  };

  const LanguageSwitcher = () => (
    <div className="language-switcher">
      {['en', 'zh', 'ja', 'ko'].map(lang => (
        <button
          key={lang}
          className={`lang-btn ${currentLang === lang ? 'active' : ''}`}
          onClick={() => i18n.changeLanguage(lang)}
        >
          {t(`languageSwitcher.${lang}`)}
        </button>
      ))}
    </div>
  );

  return (
    <div className="worldcup-page">
      <header className="worldcup-header">
        <div className="header-top">
          <h1>{t('page.title')}</h1>
          <LanguageSwitcher />
        </div>
        <p className="host-country">{t('page.host')}</p>
      </header>

      <LiveStreamSection />
      <GroupSection />
      <KnockoutTree />
      <ChampionSection />
      
      <div className="share-section">
        <div className="share-card">
          <h3>{t('share.title')}</h3>
          <p className="share-description">{t('share.description')}</p>
          
          <div className="qr-container">
            <img 
              src="/worldcup-qr-code.jpg" 
              alt="World Cup QR Code" 
              className="qr-code"
            />
          </div>
          
          <div className="share-tips">
            <h4>{t('share.tips')}</h4>
            <ul>
              <li>{t('share.tip1')}</li>
              <li>{t('share.tip2')}</li>
              <li>{t('share.tip3')}</li>
            </ul>
          </div>
          
          <div className="copy-link">
            <button 
              className="copy-btn"
              onClick={() => {
                navigator.clipboard.writeText('https://good426.asia/worldcup');
                alert(t('share.copied') || 'Link copied!');
              }}
            >
              {t('share.copyLink')}
            </button>
          </div>
        </div>
      </div>

      <footer className="worldcup-footer">
        <p>{t('footer.text')}</p>
      </footer>
    </div>
  );
};

export default WorldCupPage;
